import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-gradient-to-r from-brand-gold-dark to-brand-gold-light text-white hover:from-brand-gold-dark/90 hover:to-brand-gold-light/90 shadow-lg hover:shadow-xl transition-all duration-300 font-semibold",
        "outline-gold": "bg-gradient-to-r from-brand-gold-dark to-brand-gold-light text-white hover:from-brand-gold-dark/90 hover:to-brand-gold-light/90 shadow-lg hover:shadow-xl transition-all duration-300 font-semibold",
        "outline-blue": "border-2 border-brand-blue-dark text-brand-blue-dark bg-transparent hover:bg-brand-blue-dark hover:text-white transition-all duration-300"
      },
      size: {
        default: "h-11 px-4 py-2 min-w-[88px]", // 44px minimum height for mobile
        sm: "h-10 rounded-md px-3 min-w-[80px]", // 40px minimum for small  
        lg: "h-12 rounded-md px-8 min-w-[100px]", // 48px for large
        xl: "h-14 rounded-lg px-10 text-lg min-w-[120px]", // 56px for extra large
        icon: "h-11 w-11", // 44x44 for icon buttons - WCAG compliant
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
